import { useDebounce } from '@apps/web/hooks/use_debounce';
import { getApiClient } from '@apps/web/lib/api_client';
import { useMode } from '@apps/web/lib/mode_store';
import { useKeysetPages } from '@apps/web/swr_data_fetching/data_fetching_hooks';

export function useClientOptions(organizationId: string | null, query: string) {
    const mode = useMode();
    const search = useDebounce(query.trim(), 200);
    const validSearch =
        search === '' || (search.length >= 3 && search.length <= 100);
    const result = useKeysetPages({
        buildKey: (after) =>
            organizationId && validSearch
                ? ([
                      'coverage-client-options',
                      organizationId,
                      mode,
                      search,
                      after,
                  ] as const)
                : null,
        read: ([, orgId, requestMode, name, after]) =>
            getApiClient().get({
                url: '/organizations/{organization_id}/clients/search',
                pathParams: { organization_id: orgId },
                mode: requestMode,
                queryParams: {
                    search: name,
                    limit: 25,
                    ...(after ? { after } : {}),
                },
                reason: 'coverage-client-search',
            }),
        isReadable: !!organizationId && validSearch,
        resetToken: JSON.stringify([organizationId, mode, search]),
        swr: {
            revalidateFirstPage: false,
            revalidateOnFocus: false,
            shouldRetryOnError: false,
        },
    });
    const isDebouncing = search !== query.trim();
    return {
        ...result,
        clients:
            validSearch && !isDebouncing
                ? (result.pages?.flatMap((page) => page.clients) ?? [])
                : [],
        isLoading: result.isLoading || isDebouncing,
        hasMore: result.hasMore && validSearch && !isDebouncing,
    };
}
